import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Printer, RotateCcw, Ban, Truck } from 'lucide-react';
import { formatMoney, formatQty, PAYMENT_METHODS, UNITS, type CurrencyCode, type PaymentMethod } from '@tengesa/shared';
import { api } from '../../lib/api';
import type { SaleDetail } from '../../lib/types';
import { useSession } from '../../app/session';
import { Badge, Button, Checkbox, Dialog, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';
import { QtyInput } from '../../ui/inputs';
import { Receipt, printReceipt } from './Receipt';
import { DeliveryDialog } from '../deliveries/DeliveriesPage';

export function SaleDetailPage() {
  const { id } = useParams();
  const { can, hasModule } = useSession();
  const [delivering, setDelivering] = useState(false);
  const toast = useToast();
  const q = useQuery({ queryKey: ['sale', id], queryFn: () => api.get<SaleDetail>(`/api/sales/${id}`) });
  const [returning, setReturning] = useState(false);
  const [voiding, setVoiding] = useState(false);
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  const s = q.data!;
  const cur = s.currency as CurrencyCode;
  const money = (c: number) => formatMoney(c, cur);
  const returnable = s.status === 'completed' && s.lines.some((l) => l.qtyMilli > l.returnedMilli);
  const refunded = s.returns.reduce((a, r) => a + r.refundBaseCents, 0);

  return (
    <>
      <Link to="/sales" className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-ledger-700 hover:underline">
        <ArrowLeft className="size-4" /> All sales
      </Link>
      <PageHeader
        title={`Sale ${s.number}`}
        description={`${formatDateTime(s.createdAt)} at ${s.branch.name}, by ${s.cashier.name}`}
        actions={
          <>
            <Button icon={<Printer className="size-4" />} onClick={printReceipt}>Reprint receipt</Button>
            {hasModule('deliveries') && can('deliveries.manage') && s.status === 'completed' && !s.orderId && (
              <Button icon={<Truck className="size-4" />} onClick={() => setDelivering(true)}>Schedule delivery</Button>
            )}
            {can('pos.return') && returnable && (
              <Button icon={<RotateCcw className="size-4" />} onClick={() => setReturning(true)}>Return items</Button>
            )}
            {can('pos.void_sale') && s.status === 'completed' && s.returns.length === 0 && (
              <Button variant="danger" icon={<Ban className="size-4" />} onClick={() => setVoiding(true)}>Void sale</Button>
            )}
          </>
        }
      />
      {delivering && (
        <DeliveryDialog
          sale={{ id: s.id, number: s.number, branchId: s.branch.id, customerName: s.customer?.name ?? null, customerPhone: s.customer?.phone ?? null }}
          onClose={() => setDelivering(false)}
          onDone={() => {
            setDelivering(false);
            toast('See it on the Deliveries page');
          }}
        />
      )}
      {s.status === 'voided' && (
        <p className="mb-5 rounded-[8px] bg-danger-soft px-4 py-3 text-danger">
          Voided {formatDateTime(s.voidedAt)}: {s.voidReason}. The stock was put back and the money refunded.
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Panel title="Items">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left">
                <thead>
                  <tr className="border-b border-line text-sm text-muted">
                    <th className="px-5 py-2 font-medium">Item</th>
                    <th className="px-3 py-2 text-right font-medium">Qty</th>
                    <th className="px-3 py-2 text-right font-medium">Price</th>
                    <th className="px-3 py-2 text-right font-medium">Discount</th>
                    <th className="px-5 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {s.lines.map((l) => (
                    <tr key={l.id}>
                      <td className="px-5 py-2.5">
                        {l.productName}
                        <span className="block text-xs text-muted">
                          {l.sku}
                          {l.returnedMilli > 0 && <> — {formatQty(l.returnedMilli)} returned</>}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right num">{formatQty(l.qtyMilli)} {UNITS[l.unit as keyof typeof UNITS]?.short}</td>
                      <td className="px-3 py-2.5 text-right num">
                        {money(l.unitPriceCents)}
                        {l.unitPriceCents !== l.listPriceCents && <span className="block text-xs text-warn">was {money(l.listPriceCents)}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right text-ok num">
                        {l.promoDiscountCents + l.manualDiscountCents + l.saleDiscountCents > 0 ? `−${money(l.promoDiscountCents + l.manualDiscountCents + l.saleDiscountCents)}` : ''}
                      </td>
                      <td className="px-5 py-2.5 text-right font-medium num">{money(l.netCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dl className="space-y-1 border-t border-line px-5 py-3">
              <SumRow label="Total" value={money(s.totalCents)} strong />
              {s.business.vatRegistered && <SumRow label="VAT included" value={money(s.vatCents)} />}
              {s.costCents != null && <SumRow label="Cost of goods" value={money(s.costCents)} />}
              {s.costCents != null && <SumRow label="Gross profit" value={money(s.totalCents - s.costCents)} />}
              {refunded > 0 && <SumRow label="Refunded" value={`−${money(refunded)}`} />}
            </dl>
          </Panel>
          <Panel title="Payments">
            <ul className="divide-y divide-line">
              {s.payments.map((p) => (
                <li key={p.id} className="flex justify-between gap-3 px-5 py-2.5">
                  <span>
                    {PAYMENT_METHODS[p.method as PaymentMethod]?.label ?? p.method}
                    {p.reference && <span className="block text-sm text-muted">Ref {p.reference}</span>}
                  </span>
                  <span className="num">{formatMoney(p.amountCents, p.currency as CurrencyCode)}</span>
                </li>
              ))}
              {s.changeCents > 0 && (
                <li className="flex justify-between px-5 py-2.5 text-muted">
                  <span>Change given</span>
                  <span className="num">{formatMoney(s.changeCents, (s.changeCurrency ?? cur) as CurrencyCode)}</span>
                </li>
              )}
            </ul>
          </Panel>
          {s.returns.length > 0 && (
            <Panel title="Returns">
              <ul className="divide-y divide-line">
                {s.returns.map((r) => (
                  <li key={r.id} className="flex justify-between gap-3 px-5 py-2.5">
                    <span>
                      <span className="font-medium">{r.number}</span> <Badge>{PAYMENT_METHODS[r.refundMethod as PaymentMethod]?.label.split(' (')[0]}</Badge>
                      <span className="block text-sm text-muted">{formatDateTime(r.createdAt)} by {r.processedByName}: {r.reason}</span>
                    </span>
                    <span className="num">−{formatMoney(r.refundCents, r.refundCurrency as CurrencyCode)}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
        <div>
          <Panel title="Receipt">
            <Receipt sale={s} copy />
          </Panel>
        </div>
      </div>
      {returning && <ReturnDialog sale={s} onClose={() => setReturning(false)} />}
      {voiding && <VoidDialog sale={s} onClose={() => setVoiding(false)} />}
    </>
  );
}

function SumRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between ${strong ? 'text-lg font-semibold' : 'text-muted'}`}>
      <dt>{label}</dt>
      <dd className="num">{value}</dd>
    </div>
  );
}

const REFUND_METHODS: PaymentMethod[] = ['cash', 'card', 'ecocash', 'onemoney', 'innbucks', 'omari', 'bank_transfer'];

function ReturnDialog({ sale, onClose }: { sale: SaleDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const cur = sale.currency as CurrencyCode;
  const open = sale.lines.filter((l) => l.qtyMilli > l.returnedMilli);
  const [qty, setQty] = useState<Record<string, number | null>>({});
  const [restock, setRestock] = useState<Record<string, boolean>>(Object.fromEntries(open.map((l) => [l.id, true])));
  const [reason, setReason] = useState('');
  const methods = sale.customerId ? [...REFUND_METHODS, 'account' as PaymentMethod] : REFUND_METHODS;
  const [method, setMethod] = useState<PaymentMethod>(sale.payments.find((p) => p.method !== 'deposit')?.method as PaymentMethod ?? 'cash');
  const [reference, setReference] = useState('');
  const chosen = open.filter((l) => (qty[l.id] ?? 0) > 0);
  const estimate = chosen.reduce((a, l) => a + Math.round((l.netCents * (qty[l.id] ?? 0)) / l.qtyMilli), 0);
  const m = useMutation({
    mutationFn: () =>
      api.post('/api/returns', {
        saleId: sale.id,
        reason,
        refundMethod: method,
        reference: reference || null,
        lines: chosen.map((l) => ({ saleLineId: l.id, qtyMilli: qty[l.id], restock: restock[l.id] ?? true })),
      }),
    onSuccess: () => {
      toast('Return recorded');
      void qc.invalidateQueries({ queryKey: ['sale', sale.id] });
      void qc.invalidateQueries({ queryKey: ['sales'] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={`Return items from ${sale.number}`}
      description="Choose how many of each item came back and whether it can be sold again."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!chosen.length || reason.trim().length < 3} busy={m.isPending} onClick={() => m.mutate()}>
            Refund about {formatMoney(estimate, cur)}
          </Button>
        </>
      }
    >
      <FormError error={m.error} />
      <table className="mb-5 w-full text-left">
        <thead>
          <tr className="text-sm text-muted">
            <th className="py-1.5 font-medium">Item</th>
            <th className="py-1.5 font-medium">Can return</th>
            <th className="w-32 py-1.5 font-medium">Returning</th>
            <th className="py-1.5 font-medium">Back in stock</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {open.map((l) => (
            <tr key={l.id}>
              <td className="py-2 pr-3">{l.productName}</td>
              <td className="py-2 pr-3 num">{formatQty(l.qtyMilli - l.returnedMilli)}</td>
              <td className="py-2 pr-3">
                <QtyInput
                  unit={l.unit}
                  value={qty[l.id] ?? null}
                  onChange={(v) => setQty({ ...qty, [l.id]: v != null && v <= l.qtyMilli - l.returnedMilli ? v : null })}
                />
              </td>
              <td className="py-2">
                <Checkbox label={restock[l.id] ? 'Yes' : 'No, damaged'} checked={restock[l.id] ?? true} onChange={(v) => setRestock({ ...restock, [l.id]: v })} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Reason" htmlFor="ret-reason" error={f.reason}>
          <TextInput id="ret-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Faulty, wrong size" />
        </Field>
        <Field label="Refund by" htmlFor="ret-method" error={f.refundMethod}>
          <Select id="ret-method" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {methods.map((x) => (
              <option key={x} value={x}>{PAYMENT_METHODS[x].label}</option>
            ))}
          </Select>
        </Field>
        {PAYMENT_METHODS[method].needsReference && (
          <Field label="Refund reference" htmlFor="ret-ref" error={f.reference}>
            <TextInput id="ret-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        )}
      </div>
      <p className="mt-3 text-sm text-muted">
        Refunds are worked out from what the customer actually paid for each item, including any discount.
      </p>
    </Dialog>
  );
}

function VoidDialog({ sale, onClose }: { sale: SaleDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/api/sales/${sale.id}/void`, { reason }),
    onSuccess: () => {
      toast(`Sale ${sale.number} voided`);
      void qc.invalidateQueries({ queryKey: ['sale', sale.id] });
      void qc.invalidateQueries({ queryKey: ['sales'] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Void sale ${sale.number}?`}
      description={`All items go back into stock and ${formatMoney(sale.totalCents, sale.currency as CurrencyCode)} must be handed back to the customer. Only today's sales can be voided.`}
      footer={
        <>
          <Button onClick={onClose}>Keep sale</Button>
          <Button variant="danger" disabled={reason.trim().length < 3} busy={m.isPending} onClick={() => m.mutate()}>
            Void sale
          </Button>
        </>
      }
    >
      <FormError error={m.error} />
      <Field label="Reason" htmlFor="void-reason" hint="Recorded in the audit log.">
        <TextInput id="void-reason" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="e.g. Rung up the wrong items" />
      </Field>
    </Dialog>
  );
}
